<template>
  <div class="provenance">
    <div class="page-head">
      <div>
        <h2 class="title">
          Conversion ledger
          <a-popover placement="bottomLeft" :overlayStyle="{ maxWidth: '460px' }">
            <template slot="content">
              <div class="help">
                <p>Every model this converter produced is recorded here — where it came from, which pipeline made it, and what wire format it has. Old conversions stay on the platform, so the ledger covers the whole history, not only recent uploads.</p>
                <dl>
                  <dt>Stamped by the pipeline</dt><dd>Converted since provenance tracking. The stamps (workbook hash, component versions, schema) live inside model.json itself.</dd>
                  <dt>Inferred from the bytes</dt><dd>Older conversions. Their generation and required engine were worked out from the model's bytes by the back-fill. Re-converting the workbook produces a stamped model.</dd>
                  <dt>Generation</dt><dd>v0 · 2-slot metadata &nbsp;· v1 · stringly literals &nbsp;· v2 · typed literals &nbsp;· v3 · faithful booleans, errors and text &nbsp;· v4 · reference-function range bounds (INDEX(...):ref), engine 1.14.0+.</dd>
                  <dt>Drift</dt><dd>The same workbook (identical bytes) converted into different model content between conversions — the pipeline changed in between, or a type-fidelity upgrade landed.</dd>
                  <dt>Stamp ≠ bytes</dt><dd>A stamped value disagrees with what the model bytes look like. Should be zero; anything else needs a look.</dd>
                </dl>
              </div>
            </template>
            <a-icon type="question-circle" class="title-help" />
          </a-popover>
        </h2>
        <p class="subtitle">Where every converted model came from, and which workbooks diverged between conversions.</p>
      </div>
      <a-button icon="reload" :loading="loading" @click="load">Refresh</a-button>
    </div>

    <div class="stats">
      <div class="stat" v-for="stat in stats" :key="stat.title" :class="stat.tone">
        <div class="stat-title">{{ stat.title }}</div>
        <div class="stat-value">{{ stat.value }}</div>
        <div class="stat-note">{{ stat.note }}</div>
      </div>
    </div>

    <a-row :gutter="16">
      <a-col :span="6" v-for="dist in distributions" :key="dist.title">
        <a-card size="small" :title="dist.title">
          <div v-if="!Object.keys(dist.values).length" class="muted">—</div>
          <div v-for="[key, count] in sorted(dist.values)" :key="key" class="dist-row">
            <span class="dist-key">{{ dist.label ? dist.label(key) : key }}</span>
            <span class="dist-count">{{ count }}</span>
          </div>
        </a-card>
      </a-col>
    </a-row>

    <a-card size="small" class="drift">
      <template slot="title">
        Drifted workbooks
        <span class="card-note">— same workbook, different model content between conversions</span>
      </template>
      <a-table rowKey="sourceSha256" :columns="driftColumns" :data-source="drift" :loading="loading" size="small"
        :pagination="{ pageSize: 10 }">
        <template slot="workbook" slot-scope="text, group">
          <span>{{ group.filename || '(unknown filename)' }}</span>
          <a class="hash" @click="copy(group.sourceSha256)" title="Copy source hash">{{ short(group.sourceSha256) }}</a>
        </template>
        <template slot="count" slot-scope="text, group">{{ group.artifacts.length }}</template>
        <template slot="expandedRowRender" slot-scope="group">
          <a-table rowKey="processId" :columns="artifactColumns" :data-source="group.artifacts" size="small" :pagination="false">
            <template slot="name" slot-scope="text, a">
              <span>{{ a.name || a.processId }}</span>
              <a class="hash" @click="copy(a.processId)" title="Copy process id">{{ short(a.processId) }}</a>
            </template>
            <template slot="generation" slot-scope="text, a"><a-tag :color="generationColor(a.generation)">{{ a.generation }}</a-tag></template>
            <template slot="pipeline" slot-scope="text, a">{{ pipelineText(a.pipeline) }}</template>
            <template slot="content" slot-scope="text, a">
              <a class="hash" @click="copy(a.contentSha256)" title="Copy content hash">{{ short(a.contentSha256) }}</a>
            </template>
            <template slot="convertedAt" slot-scope="text, a">{{ a.convertedAt ? beautifyDate(a.convertedAt) : '—' }}</template>
          </a-table>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import moment from "moment";

// v4 (the current wire format) in the brand indigo; unknown generations get a
// dark neutral instead of antd's faint "default" tag
const GENERATION_COLORS = { v0: "orange", v1: "orange", v2: "blue", v3: "green", v4: "#222377" };
const GENERATION_LABELS = {
  v0: "v0 · 2-slot metadata",
  v1: "v1 · stringly literals",
  v2: "v2 · typed literals",
  v3: "v3 · faithful types",
  v4: "v4 · reference range bounds",
};

export default {
  data() {
    return {
      loading: false,
      summary: { processes: 0, withProvenance: 0, stamped: 0, inconsistent: 0, withSource: 0, byGeneration: {}, byRequiredEngine: {}, byTypeFidelity: {}, byPreprocessor: {}, byExtractor: {} },
      drift: [],
      driftColumns: [
        { title: "Workbook", key: "workbook", scopedSlots: { customRender: "workbook" } },
        { title: "Conversions", key: "count", width: 120, scopedSlots: { customRender: "count" } },
      ],
      artifactColumns: [
        { title: "Conversion", key: "name", scopedSlots: { customRender: "name" } },
        { title: "Generation", key: "generation", width: 110, scopedSlots: { customRender: "generation" } },
        { title: "Pipeline", key: "pipeline", scopedSlots: { customRender: "pipeline" } },
        { title: "Content hash", key: "content", width: 130, scopedSlots: { customRender: "content" } },
        { title: "Converted", key: "convertedAt", width: 150, scopedSlots: { customRender: "convertedAt" } },
      ],
    };
  },
  computed: {
    stats() {
      const s = this.summary;
      const withoutModel = s.processes - s.withProvenance;
      const inferred = s.withProvenance - s.stamped;
      return [
        {
          title: "Conversions in the archive",
          value: s.processes,
          note: `${s.withProvenance} produced a model · ${withoutModel} failed or have no model`,
        },
        {
          title: "Stamped by the pipeline",
          value: s.stamped,
          note: "converted since provenance tracking — stamps live in model.json",
        },
        {
          title: "Inferred from the bytes",
          value: inferred,
          note: "older conversions classified by the back-fill — re-convert to stamp",
        },
        {
          title: "Stamp ≠ bytes",
          value: s.inconsistent,
          note: s.inconsistent ? "stamps disagree with the model content — investigate" : "no stamp disagrees with its model content",
          tone: s.inconsistent ? "bad" : "",
        },
        {
          title: "Drifted workbooks",
          value: this.drift.length,
          note: "same workbook, different model content — listed below",
          tone: this.drift.length ? "warn" : "",
        },
      ];
    },
    distributions() {
      return [
        { title: "Generation (wire format)", values: this.summary.byGeneration, label: (k) => GENERATION_LABELS[k] || k },
        { title: "Engine required", values: this.summary.byRequiredEngine, label: (k) => (k === "unknown" ? "unknown schema" : `≥ ${k}`) },
        { title: "Extractor version", values: this.summary.byExtractor, label: (k) => (k === "unknown" ? "unstamped" : k) },
        { title: "Preprocessor version", values: this.summary.byPreprocessor, label: (k) => (k === "unknown" ? "unstamped" : k) },
      ];
    },
  },
  methods: {
    async load() {
      this.loading = true;
      const headers = { Authorization: this.$store.state.auth.token };
      try {
        const [summary, drift] = await Promise.all([
          this.$http.get("/api/process/provenance/summary", { headers }),
          this.$http.get("/api/process/provenance/drift", { headers }),
        ]);
        this.summary = summary.data;
        this.drift = drift.data.drift;
      } catch (error) {
        console.error(error);
        this.$message.error("Could not load the conversion ledger");
      } finally {
        this.loading = false;
      }
    },
    sorted(values) {
      return Object.entries(values).sort((a, b) => b[1] - a[1]);
    },
    short(hash) {
      return hash ? hash.slice(0, 10) + "…" : "—";
    },
    generationColor(generation) {
      return GENERATION_COLORS[generation] || "#595959";
    },
    pipelineText(pipeline) {
      const entries = Object.entries(pipeline || {});
      return entries.length ? entries.map(([k, v]) => `${k} ${v}`).join(" · ") : "unstamped";
    },
    beautifyDate(date) {
      return moment(date).format("HH:mm DD/MM/YYYY");
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success("Copied");
      } catch (e) {
        this.$message.info(text);
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.provenance { display: flex; flex-direction: column; gap: 20px; overflow: auto; }
.page-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.title { margin: 0; font-size: 22px; font-weight: 700; color: rgba(0, 0, 0, 0.85); display: flex; align-items: center; gap: 8px; }
.title-help { font-size: 16px; color: #222377; opacity: 0.7; cursor: help; }
.subtitle { margin: 4px 0 0; font-size: 14px; font-weight: 400; color: rgba(0, 0, 0, 0.55); }
.help p { margin: 0 0 8px; font-weight: 400; }
.help dl { display: grid; grid-template-columns: 140px 1fr; gap: 6px 12px; margin: 0; font-weight: 400; }
.help dt { font-weight: 600; }
.help dd { margin: 0; }

.stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
.stat { background: #fff; border: 1px solid #e8e8e8; border-radius: 4px; padding: 12px 16px; display: flex; flex-direction: column; gap: 2px; }
.stat-title { font-size: 12px; font-weight: 600; letter-spacing: 0.02em; color: rgba(0, 0, 0, 0.55); }
.stat-value { font-size: 28px; font-weight: 700; line-height: 1.2; color: rgba(0, 0, 0, 0.85); font-variant-numeric: tabular-nums; }
.stat-note { font-size: 12px; font-weight: 400; color: rgba(0, 0, 0, 0.45); }
.stat.warn .stat-value { color: #d48806; }
.stat.bad .stat-value { color: #cf1322; }

.dist-row { display: flex; justify-content: space-between; padding: 2px 0; font-variant-numeric: tabular-nums; font-weight: 400; }
.dist-key { color: rgba(0, 0, 0, 0.75); }
.dist-count { font-weight: 600; }
.card-note { font-weight: 400; color: rgba(0, 0, 0, 0.45); }
.muted { color: rgba(0, 0, 0, 0.35); }
.hash { font-family: "SFMono-Regular", Menlo, Consolas, monospace; font-size: 12px; margin-left: 8px; }
</style>
